/**
 * BMR-only calculator. Reuses formulas from calorie.ts and adds Cunningham.
 */
import {
  mifflinBMR,
  harrisBMR,
  katchBMR,
  ACTIVITY_MULTIPLIER,
  type Gender,
  type Activity,
} from "./calorie";

export type BMRFormula = "mifflin" | "harris" | "katch" | "cunningham";

export function cunninghamBMR(kg: number, bodyFatPct: number): number {
  const lbm = kg * (1 - bodyFatPct / 100);
  return 500 + 22 * lbm;
}

export interface BMRInput {
  gender: Gender;
  age: number;
  heightCm: number;
  weightKg: number;
  bodyFatPct?: number;
}

export interface BMRBreakdown {
  mifflin: number;
  harris: number;
  katch: number | null;
  cunningham: number | null;
}

export function computeAllBMR(inp: BMRInput): BMRBreakdown {
  const { gender, age, heightCm, weightKg, bodyFatPct } = inp;
  const hasBF = bodyFatPct != null && Number.isFinite(bodyFatPct);
  return {
    mifflin: Math.round(mifflinBMR(gender, weightKg, heightCm, age)),
    harris: Math.round(harrisBMR(gender, weightKg, heightCm, age)),
    katch: hasBF ? Math.round(katchBMR(weightKg, bodyFatPct!)) : null,
    cunningham: hasBF ? Math.round(cunninghamBMR(weightKg, bodyFatPct!)) : null,
  };
}

export function selectedBMR(b: BMRBreakdown, f: BMRFormula): number | null {
  if (f === "mifflin") return b.mifflin;
  if (f === "harris") return b.harris;
  if (f === "katch") return b.katch;
  return b.cunningham;
}

export function activityPreview(bmr: number): Record<Activity, number> {
  return {
    sedentary: Math.round(bmr * ACTIVITY_MULTIPLIER.sedentary),
    light: Math.round(bmr * ACTIVITY_MULTIPLIER.light),
    moderate: Math.round(bmr * ACTIVITY_MULTIPLIER.moderate),
    very: Math.round(bmr * ACTIVITY_MULTIPLIER.very),
    extra: Math.round(bmr * ACTIVITY_MULTIPLIER.extra),
  };
}

export type BMRError = "age_invalid" | "height_invalid" | "weight_invalid" | "bf_invalid" | "missing";

export function validateBMR(
  inp: Partial<BMRInput> & { formula?: BMRFormula },
): { ok: true } | { ok: false; error: BMRError } {
  const { age, heightCm, weightKg, bodyFatPct, formula } = inp;
  if (age == null || heightCm == null || weightKg == null) return { ok: false, error: "missing" };
  if (!Number.isFinite(age) || age < 10 || age > 100) return { ok: false, error: "age_invalid" };
  if (!Number.isFinite(heightCm) || heightCm < 80 || heightCm > 260) return { ok: false, error: "height_invalid" };
  if (!Number.isFinite(weightKg) || weightKg < 25 || weightKg > 350) return { ok: false, error: "weight_invalid" };
  if ((formula === "katch" || formula === "cunningham")) {
    if (bodyFatPct == null || !Number.isFinite(bodyFatPct) || bodyFatPct < 3 || bodyFatPct > 60) {
      return { ok: false, error: "bf_invalid" };
    }
  }
  return { ok: true };
}
